import React from 'react';
import { Calendar, User, Building2, PoundSterling } from 'lucide-react';
import type { Deal, TeamMember } from '../../types';

interface DealCardProps {
  deal: Deal;
  members: TeamMember[];
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const getFollowUpClass = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  if (date < now) return 'bg-red-500/20 text-red-400 border-red-500/30';
  const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
  return 'bg-slate-700/50 text-slate-400 border-slate-600';
};

const DealCard: React.FC<DealCardProps> = ({ deal, members, onClick, onDragStart }) => {
  const owner = members.find((m) => m.user_id === deal.owner_user_id);
  const ownerInitials = owner?.email
    ? owner.email.split('@')[0].slice(0, 2).toUpperCase()
    : '';

  const contactName = deal.contact
    ? `${deal.contact.first_name || ''} ${deal.contact.last_name || ''}`.trim() || deal.contact.company
    : null;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="bg-slate-800 border border-slate-700 rounded-lg p-3 cursor-pointer hover:border-slate-600 hover:bg-slate-750 transition-all group"
    >
      <h4 className="font-medium text-white text-sm mb-2 line-clamp-2">{deal.title}</h4>

      {contactName && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
          <Building2 size={12} />
          <span className="truncate">{contactName}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {deal.value_monthly && (
            <span className="flex items-center gap-0.5 text-xs text-emerald-400">
              <PoundSterling size={10} />
              {deal.value_monthly.toLocaleString()}
            </span>
          )}

          {deal.next_follow_up && (
            <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border ${getFollowUpClass(deal.next_follow_up)}`}>
              <Calendar size={10} />
              {formatDate(deal.next_follow_up)}
            </span>
          )}
        </div>

        {ownerInitials && (
          <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs font-medium text-slate-300">
            {ownerInitials}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealCard;
